import { PaymentMethod } from '@/generated/gql/graphql'
import { numberFormat } from '@/helpers/numbers'
import { isBefore } from 'date-fns'
import { useEffect, useState } from 'react'
import { useInterval } from 'react-use'

type Props = {
  changesMade: boolean
  minimumOrderValue: number
  userEditCutOffTime: Date | null
  subtotalExTax: number
  paymentMethod?: PaymentMethod
}

export type ExistingOrderValidationErrors = Partial<{
  PAST_EDIT_CUT_OFF: { cutOffTime: Date }
  MINIMUM_SPEND_NOT_MET: { minimumOrderValue: string; amountRequired: string }
  INVALID_PAYMENT_METHOD: { paymentMethod: PaymentMethod }
}>

export const useExistingOrderValidation = ({
  changesMade,
  minimumOrderValue,
  subtotalExTax,
  userEditCutOffTime,
  paymentMethod,
}: Props) => {
  const [errors, setErrors] = useState<ExistingOrderValidationErrors>({})

  useInterval(() => {
    if (!userEditCutOffTime) return
    const isPastEditCutOff = isBefore(userEditCutOffTime, new Date())
    setErrors((prev) => {
      const hasPastCutOffError = Boolean(prev.PAST_EDIT_CUT_OFF)

      if (!isPastEditCutOff) {
        if (!hasPastCutOffError) return prev
        const nextErrors = { ...prev }
        delete nextErrors.PAST_EDIT_CUT_OFF
        return nextErrors
      }

      const existingTime = prev.PAST_EDIT_CUT_OFF?.cutOffTime?.getTime()
      if (hasPastCutOffError && existingTime === userEditCutOffTime.getTime()) return prev

      return {
        ...prev,
        PAST_EDIT_CUT_OFF: { cutOffTime: userEditCutOffTime },
      }
    })
  }, 3000)

  useEffect(() => {
    if (subtotalExTax < minimumOrderValue) {
      const remainingAmountRequired = minimumOrderValue - subtotalExTax
      setErrors((prev) => ({
        ...prev,
        MINIMUM_SPEND_NOT_MET: {
          minimumOrderValue: numberFormat(minimumOrderValue / 100),
          amountRequired: numberFormat(remainingAmountRequired / 100),
        },
      }))
    } else
      setErrors((prev) => {
        const newState = { ...prev, MINIMUM_SPEND_NOT_MET: undefined }
        delete newState.MINIMUM_SPEND_NOT_MET
        return newState
      })
  }, [subtotalExTax, minimumOrderValue])

  useEffect(() => {
    if (!paymentMethod) return
    if (paymentMethod === PaymentMethod.Card)
      setErrors((prev) => ({
        ...prev,
        INVALID_PAYMENT_METHOD: { paymentMethod },
      }))
  }, [paymentMethod])

  const canSaveChanges =
    changesMade &&
    (Object.keys(errors).length === 0 || Object.values(errors).some((error) => !error))

  return {
    errors,
    canSaveChanges,
  }
}

export type UseExistingOrderValidationResult = ReturnType<typeof useExistingOrderValidation>
